import { noop } from 'lodash';
import React from 'react';
import { useStyles } from 'uinix-ui';

import { useToggle } from '../../hooks/useToggle';
import { Input } from '../../types/Input';
import { Option } from '../../types/Option';
import { stopPropagation } from '../../utils/event';
import { Box } from '../Box/Box';
import { Icon } from '../Icon/Icon';
import { Layout } from '../Layout/Layout';
import { MarkedText } from '../MarkedText/MarkedText';
import { Text } from '../Text/Text';
import { Tooltip } from '../Tooltip/Tooltip';

interface Props extends Input<boolean> {
  /** Radio options */
  option: Option;
  /** Boolean to determine if radio is checked */
  checked: boolean;
  /** Supported label modes */
  labelMode?: 'hidden';
  /** Label typography variant */
  labelVariant?: 'body' | 'body-medium';
  /** Used to highlight radio label if it matches search */
  search?: string;
}

export const Radio = ({
  checked,
  disabled,
  labelMode,
  labelVariant,
  name,
  option,
  search,
  onChange,
}: Props) => {
  const styles = useStyles();

  const [focused, _toggle, focus, blur] = useToggle();

  const { info, label, value, tooltip, description } = option;
  const radioValue = String(value);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    onChange(event.target.checked);

  const labelText = (
    <Text
      styles={labelMode === 'hidden' ? styles.hidden.visual : null}
      variant={labelVariant}
    >
      <MarkedText search={search} text={label} />
    </Text>
  );

  return (
    <Tooltip tooltip={tooltip}>
      <Layout
        data-eds-styles-disable-interactable
        as="label"
        disabled={disabled}
        spacing={1}
        styles={componentStyles.base}
        onClick={stopPropagation}
      >
        <input
          checked={checked}
          disabled={disabled}
          name={name}
          type="radio"
          value={radioValue}
          onBlur={blur}
          onChange={handleChange}
          onFocus={focus}
        />
        <Box
          styles={
            focused
              ? {
                  ...styles.interactions[':focus'],
                  ...styles.focus.ring,
                  height: 'fit-content',
                }
              : { height: 'fit-content' }
          }
          onClick={noop}
        >
          <Icon icon={checked ? 'radio-checked' : 'radio-empty'} />
        </Box>
        <Layout direction="column" spacing={1}>
          <Layout spacing={1}>
            {labelText}
            {info && <Icon icon="info" tooltip={info} />}
          </Layout>
          {description && <Text preset="description">{description}</Text>}
        </Layout>
      </Layout>
    </Tooltip>
  );
};

export const componentStyles = {
  base: {
    userSelect: 'none',
    '> input': {
      opacity: 0,
      position: 'absolute' as const,
      width: 0,
    },
  },
};
